import { useState } from "react";
import { t } from "ttag";

import { getErrorMessage } from "metabase/api/utils/errors";
import { Alert, Icon, Stack, Switch, Text } from "metabase/ui";
import type { GitSyncSettings } from "metabase-types/api";

import { useUpdateGitSyncPermissionsMutation } from "../api";

type SyncedPermissionsSwitchProps = {
  settings: GitSyncSettings;
};

/**
 * Turns syncing of groups and their permissions on or off. Every instance
 * that takes part needs it on, the read-only ones included.
 */
export function SyncedPermissionsSwitch({
  settings,
}: SyncedPermissionsSwitchProps) {
  const [updatePermissions, { isLoading }] =
    useUpdateGitSyncPermissionsMutation();
  const [updateError, setUpdateError] = useState<unknown>(null);

  const handleChange = async (synced: boolean) => {
    setUpdateError(null);
    const result = await updatePermissions({ synced });
    if (result.error) {
      setUpdateError(result.error);
    }
  };

  return (
    <Stack gap="sm">
      {updateError != null && (
        <Alert color="error" icon={<Icon name="warning" />}>
          {getErrorMessage(updateError)}
        </Alert>
      )}
      <Switch
        label={t`Sync groups and permissions`}
        checked={settings.sync_permissions}
        disabled={isLoading}
        onChange={(event) => handleChange(event.currentTarget.checked)}
      />
      {settings.type === "read-only" && settings.sync_permissions && (
        <Text c="text-secondary">
          {t`The groups on the branch, and their permissions, can't be edited here.`}
        </Text>
      )}
    </Stack>
  );
}
